import React, { useState, useEffect, useCallback } from 'react';
import {  FaCheckCircle, FaCross } from 'react-icons/fa';
import '../styles/CompliancePage.css';
import complianceService from '../services/complianceService';
import assetService from '../services/assetService';
import LabTechnicianSidebar from '../components/Techniciansidebar';

const CompliancePage = () => {
  const [activeTab, setActiveTab] = useState('due');
  const [daysFilter, setDaysFilter] = useState(30);
  const [assetsDue, setAssetsDue] = useState([]);
  const [assetsCompleted, setAssetsCompleted] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [editForm, setEditForm] = useState({
    complianceDate: '',
    notes: ''
  });
  
  // Compliance Detail Modal States
  const [detailModal, setDetailModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [maintenanceLogId, setMaintenanceLogId] = useState(null); // Store maintenance log ID for API calls
  const [complianceChecklistItems, setComplianceChecklistItems] = useState([]);
  const [complianceRemarks, setComplianceRemarks] = useState('');
  const [complianceStage, setComplianceStage] = useState('pending'); // pending -> accepted -> in_progress -> completed
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Fetch data on mount and when tab/filter changes
  const fetchComplianceData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch due assets
      const dueResponse = await complianceService.getComplianceDueSoon(daysFilter);
      setAssetsDue(dueResponse.data || []);

      // Fetch completed assets
      const completedResponse = await complianceService.getComplianceAssets('completed');
      setAssetsCompleted(completedResponse.data || []);


    } catch (error) {
      console.error('Error fetching compliance data:', error);
    } finally {
      setLoading(false);
    }
  }, [daysFilter]);

  useEffect(() => {
    fetchComplianceData();
  }, [fetchComplianceData, activeTab, daysFilter]);

  const handleOpenEdit = (asset) => {
    setEditingAsset(asset);
    setEditForm({
      complianceDate: asset.complianceDate ? asset.complianceDate.split('T')[0] : '',
      notes: asset.complianceNotes || ''
    });
    setEditModal(true);
  };

  const handleCloseEditModal = () => {
    setEditModal(false);
    setEditingAsset(null);
    setEditForm({ complianceDate: '', notes: '' });
  };

  const handleSaveCompliance = async () => {
    try {
      await assetService.updateAsset(editingAsset._id, {
        complianceDate: editForm.complianceDate,
        complianceNotes: editForm.notes
      });
      handleCloseEditModal();
      fetchComplianceData();
    } catch (error) {
      console.error('Error saving compliance:', error);
    }
  };

  const handleRevertToDue = async (asset) => {
    try {
      // Set compliance date to 30 days from today
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 30);
      
      await assetService.updateAsset(asset._id, {
        complianceDate: futureDate.toISOString().split('T')[0],
        isComplianceCompleted: false
      });
      
      // Refresh data and switch to due tab
      await fetchComplianceData();
      setActiveTab('due');
    } catch (error) {
      console.error('Error reverting to due:', error);
    }
  };

  // Compliance Detail Modal Handlers
  const handleOpenComplianceDetail = async (asset) => {
    try {
      setLoading(true);
     
      
      // Fetch full asset data to ensure we have all fields including complianceChecklists
      const fullAssetResponse = await assetService.getAssetById(asset._id);
     
      
      const fullAsset = fullAssetResponse.data?.data || fullAssetResponse.data || asset;
    
      
      // Try to find maintenance log for this asset
      let foundLog = null;
      try {
        const maintenanceResponse = await fetch(`/api/maintenance/asset/${asset._id}`, {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        });
        if (maintenanceResponse.ok) {
          const maintenanceData = await maintenanceResponse.json();
          foundLog = maintenanceData.data?.find(log => log.maintenanceType === 'compliance' && log.status !== 'completed');
          if (foundLog) {
          
            setMaintenanceLogId(foundLog._id);
            setComplianceChecklistItems(foundLog.checklist || []);
            setComplianceStage(foundLog.status || 'pending');
            setSelectedAsset(fullAsset);
            setDetailModal(true);
            
            setLoading(false);
            return;
          }
        }
      } catch (maintenanceError) {
        console.log('⚠️ Could not fetch maintenance log:', maintenanceError.message);
      }
      
      // If no maintenance log found, CREATE ONE automatically
      if (!foundLog) {
        try {
      
          const checklistItems = fullAsset.complianceChecklists || [];
          const createResponse = await fetch('/api/maintenance', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${sessionStorage.getItem('token')}`
            },
            body: JSON.stringify({
              assetId: asset._id,
              maintenanceType: 'compliance',
              checklist: checklistItems.map(item => ({
                text: typeof item === 'string' ? item : item.text,
                completed: item.completed || false
              })),
              priorityLevel: 'high',
              expectedCompletionDate: fullAsset.complianceDate
            })
          });

          if (createResponse.ok) {
            const newLog = await createResponse.json();
            const maintenanceLogIdFromResponse = newLog.data?._id || newLog.data?.maintenanceLog?._id;
        
            setMaintenanceLogId(maintenanceLogIdFromResponse);
            setComplianceChecklistItems(checklistItems);
            setComplianceStage('pending');
            setSelectedAsset(fullAsset);
            setDetailModal(true);
          
            setLoading(false);
            return;
          } else {
          
          }
        } catch (createError) {
          console.error('❌ Error creating maintenance log:', createError);
        }
      }
      
      // Fallback to asset data if maintenance log creation failed

      setSelectedAsset(fullAsset);
      setComplianceChecklistItems(fullAsset.complianceChecklists || []);
      setComplianceRemarks('');
      const currentStage = fullAsset.complianceStage || 'pending';
      setComplianceStage(currentStage);
   
      // DO NOT set maintenanceLogId - keep it null to indicate fallback mode
      setDetailModal(true);
      
  
    } catch (error) {
  
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Fallback to asset data if fetch fails
     
      setSelectedAsset(asset);
      setComplianceChecklistItems(asset.complianceChecklists || []);
      setComplianceRemarks('');
      setComplianceStage(asset.complianceStage || 'pending');
      setDetailModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDetailModal = () => {
    setDetailModal(false);
    setSelectedAsset(null);
    setMaintenanceLogId(null);
    setComplianceChecklistItems([]);
    setComplianceRemarks('');
    setComplianceStage('pending');
    setShowRejectModal(false);
    setRejectionReason('');
  };

  // Stage Progression Handlers
  const handleAcceptCompliance = async () => {
    if (!maintenanceLogId) {
      alert('❌ Maintenance log ID not found');
      return;
    }

    try {
      setLoading(true);
      
      const response = await fetch(`/api/maintenance/${maintenanceLogId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to accept compliance');
      }

     
      setComplianceStage('accepted');
      await fetchComplianceData();
      alert('✓ Compliance accepted! You can now start checking the items.');
    } catch (error) {
      console.error('❌ Error accepting compliance:', error);
      alert(`Failed to accept compliance: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectCompliance = async () => {
    if (!maintenanceLogId) {
      alert('❌ Maintenance log ID not found');
      return;
    }

    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      setLoading(true);
      console.log('❌ Rejecting compliance with reason:', rejectionReason);
      const response = await fetch(`/api/maintenance/${maintenanceLogId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({ reason: rejectionReason })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reject compliance');
      }

 
    
      alert('✓ Compliance rejected! Admin will review and may reopen the task.');
      setShowRejectModal(false);
      setRejectionReason('');
      handleCloseDetailModal();
      await fetchComplianceData();
    } catch (error) {

      alert(`Failed to reject compliance: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleComplianceChecklistItem = async (index) => {
    if (!maintenanceLogId) {
      alert('❌ Maintenance log ID not found');
      return;
    }

    const updated = [...complianceChecklistItems];
    updated[index].completed = !updated[index].completed;
    setComplianceChecklistItems(updated);

    // Auto-save to backend - this will trigger auto-transitions
    try {
      const response = await fetch(`/api/maintenance/${maintenanceLogId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({ checklist: updated })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update checklist');
      }

      const result = await response.json();


      // If status auto-updated, update the UI
      if (result.autoStatusUpdate && result.newStatus !== complianceStage) {
        setComplianceStage(result.newStatus);
        if (result.newStatus === 'in_progress') {
          alert('✅ Compliance auto-marked as In Progress!');
        } else if (result.newStatus === 'completed') {
          alert('🎉 Compliance auto-completed! All checklist items done.');
          handleCloseDetailModal();
          await fetchComplianceData();
          setActiveTab('completed');
        }
      }
    } catch (error) {
    
      alert(`Failed to update checklist: ${error.message}`);
      // Revert the change
      updated[index].completed = !updated[index].completed;
      setComplianceChecklistItems(updated);
    }
  };

  const handleRemoveComplianceChecklistItem = (index) => {
    setComplianceChecklistItems(complianceChecklistItems.filter((_, i) => i !== index));
  };

  // Manual Mark as In Progress (when user clicks button in accepted stage)
  const handleMarkAsInProgress = async () => {
    if (!maintenanceLogId) {
      alert('❌ Maintenance log ID not found');
      return;
    }

    try {
      setLoading(true);
    
      const response = await fetch(`/api/maintenance/${maintenanceLogId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: 'in_progress' })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to mark as in progress');
      }

    
      setComplianceStage('in_progress');
      alert('✅ Compliance marked as In Progress! Continue checking items.');
    } catch (error) {
     
      alert(`Failed to mark as in progress: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Manual Mark as Complete (when user clicks button in in_progress stage)
  const handleMarkAsComplete = async () => {
    if (!maintenanceLogId) {
      alert('❌ Maintenance log ID not found');
      return;
    }

    const uncheckedCount = complianceChecklistItems.filter(item => !item.completed).length;
    if (uncheckedCount > 0) {
      const confirmComplete = window.confirm(
        `⚠️ ${uncheckedCount} item(s) still unchecked. Mark as complete anyway?`
      );
      if (!confirmComplete) return;
    }

    try {
      setLoading(true);
     
      const response = await fetch(`/api/maintenance/${maintenanceLogId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: 'completed' })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to mark as complete');
      }

     
     
      setComplianceStage('completed');
      alert('🎉 Compliance task completed successfully!');
      handleCloseDetailModal();
      await fetchComplianceData();
      setActiveTab('completed');
    } catch (error) {
     
      alert(`Failed to mark as complete: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getComplianceChecklistProgress = () => {
    if (complianceChecklistItems.length === 0) return 0;
    const completed = complianceChecklistItems.filter(item => item.completed).length;
    return Math.round((completed / complianceChecklistItems.length) * 100);
  };

  const getComplianceStatus = (asset) => {
    // If we're on the completed tab, return 'completed'
    if (activeTab === 'completed') {
      return 'completed';
    }

    if (!asset.complianceDate) return 'no-date';
    
    const today = new Date();
    const complianceDate = new Date(asset.complianceDate);
    const daysUntil = Math.floor((complianceDate - today) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) return 'overdue';
    if (daysUntil <= 7) return 'critical';
    if (daysUntil <= 14) return 'warning';
    return 'upcoming';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntil = (dateString) => {
    if (!dateString) return 'N/A';
    const today = new Date();
    const complianceDate = new Date(dateString);
    const days = Math.floor((complianceDate - today) / (1000 * 60 * 60 * 24));
    return days;
  };

  const currentData = activeTab === 'due' ? assetsDue : assetsCompleted;

  return (
    <div className="compliance-page">
      <LabTechnicianSidebar />
      <div className="page-container">
        <div className="compliance-dashboard">
          {/* Header */}
          <div className="compliance-header">
            <h2>Compliance Management</h2>
            <p>Track and manage compliance dates for all assets</p>
          </div>

       
          {/* Tabs */}
          <div className="compliance-subtab-bar">
            <button
              className={`compliance-subtab ${activeTab === 'due' ? 'active' : ''}`}
              onClick={() => setActiveTab('due')}
            >
              <FaCross /> Assets Due
            </button>
            <button
              className={`compliance-subtab ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => setActiveTab('completed')}
            >
              <FaCheckCircle /> Completed
            </button>
          </div>

          {/* Filter */}
          {activeTab === 'due' && (
            <div className="compliance-filter-section">
              <div className="compliance-filter-group">
                <label htmlFor="days-filter">Filter by Days:</label>
                <select
                  id="days-filter"
                  value={daysFilter}
                  onChange={(e) => setDaysFilter(Number(e.target.value))}
                >
                  <option value={7}>Next 7 days</option>
                  <option value={14}>Next 14 days</option>
                  <option value={30}>Next 30 days</option>
                  <option value={90}>Next 90 days</option>
                </select>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="compliance-container">
            {loading ? (
              <div className="loading">
                <span className="spin">🔄</span>
                Loading compliance data...
              </div>
            ) : currentData.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <div className="empty-title">
                  {activeTab === 'due' ? 'No Due Dates' : 'No Completed Assets'}
                </div>
                <div className="empty-message">
                  {activeTab === 'due'
                    ? 'All assets are compliant!'
                    : 'No completed compliance records yet.'}
                </div>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="compliance-table">
                  <thead>
                    <tr>
                      <th>Asset Name</th>
                      <th>Serial #</th>
                      <th>Held By</th>
                      <th>Compliance Date</th>
                      {activeTab === 'due' && <th>Days Left</th>}
                      <th>Status</th>
                      <th>Workflow Stage</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((asset) => {
                      const status = getComplianceStatus(asset);
                      const rowClass = status === 'critical' ? 'critical-row' : status === 'warning' ? 'warning-row' : '';

                      return (
                        <tr key={asset._id} className={rowClass}>
                          <td>
                            <div className="holder">
                              <span className="asset-name">{asset.assetName}</span>
                              <span className="asset-serial">{asset.serialNumber}</span>
                            </div>
                          </td>
                          <td>{asset.serialNumber}</td>
                          <td>
                            <div className="holder">
                              <span className="holder-name">
                                {asset.currentHolder?.firstName} {asset.currentHolder?.lastName}
                              </span>
                              <span className="holder-email">{asset.currentHolder?.email}</span>
                            </div>
                          </td>
                          <td>{formatDate(asset.complianceDate)}</td>
                          {activeTab === 'due' && (
                            <td>
                              <span className={`days ${status}`}>
                                {getDaysUntil(asset.complianceDate)} days
                              </span>
                            </td>
                          )}
                          <td>
                            <span className={`badge ${status}`}>
                              {status === 'critical' ? 'Critical' : status === 'warning' ? 'Warning' : status === 'upcoming' ? 'Upcoming' : status === 'completed' ? 'Completed' : 'Overdue'}
                            </span>
                          </td>
                          <td>
                            <span className={`stage-badge stage-${asset.complianceStage || 'pending'}`}>
                              {asset.complianceStage === 'pending' ? '⏳ Pending' : asset.complianceStage === 'accepted' ? '✓ Accepted' : asset.complianceStage === 'in_progress' ? '⚙️ In Progress' : asset.complianceStage === 'completed' ? '✅ Completed' : '⏳ Pending'}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {activeTab === 'due' ? (
                                <>
                                  <button
                                    className="btn-completed"
                                    onClick={() => handleOpenComplianceDetail(asset)}
                                    title="View compliance details and checklist"
                                  >
                                    📋 Details
                                  </button>
                                  <button
                                    className="btn-edit"
                                    onClick={() => handleOpenEdit(asset)}
                                    title="Edit compliance date"
                                  >
                                    ✏️ Edit
                                  </button>
                                </>
                              ) : (
                                <button
                                  className="btn-revert"
                                  onClick={() => handleRevertToDue(asset)}
                                  title="Mark as pending compliance"
                                >
                                  ↩️ Revert
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-icon">📝</div>
                <div>
                  <h3>Edit Compliance Date</h3>
                  <div className="modal-subtitle">{editingAsset?.assetName}</div>
                </div>
              </div>
              <button className="close-btn" onClick={handleCloseEditModal}>✕</button>
            </div>

            <div className="modal-body">
              <div className="edit-form">
                <div className="form-group">
                  <label>Compliance Date</label>
                  <input
                    type="date"
                    className="date-input"
                    value={editForm.complianceDate}
                    onChange={(e) => setEditForm({ ...editForm, complianceDate: e.target.value })}
                  />
                  <div className="info-box">
                    ℹ️ Set the date when this asset must be compliance-checked
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes (Optional)</label>
                  <textarea
                    className="date-input"
                    rows="4"
                    placeholder="Add any compliance-related notes..."
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    style={{ fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCloseEditModal}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleSaveCompliance}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Detail Modal */}
      {detailModal && selectedAsset && (
        <div className="modal-overlay" onClick={handleCloseDetailModal}>
          <div className="modal-content issue-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <span className="modal-icon">📋</span>
                <div>
                  <h3>
                    Compliance Details
                    <span className={`stage-badge stage-${complianceStage || 'pending'}`} style={{ marginLeft: '12px', fontSize: '12px', padding: '4px 10px' }}>
                      {complianceStage === 'pending' ? '⏳ Pending' : complianceStage === 'accepted' ? '✓ Accepted' : complianceStage === 'in_progress' ? '⚙️ In Progress' : complianceStage === 'completed' ? '✅ Completed' : '⏳ Pending'}
                    </span>
                  </h3>
                  <span className="modal-subtitle">{selectedAsset.assetName}</span>
                </div>
              </div>
              <button
                className="close-btn"
                onClick={handleCloseDetailModal}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Compliance Stage Indicator */}
              <div className="stage-indicator">
                <div className={`stage-step ${complianceStage === 'pending' || complianceStage === 'accepted' || complianceStage === 'in_progress' || complianceStage === 'completed' ? 'active' : ''} ${complianceStage !== 'pending' ? 'completed' : ''}`}>
                  <div className="stage-circle">1</div>
                  <div className="stage-label">Pending</div>
                </div>
                <div className="stage-connector"></div>
                <div className={`stage-step ${complianceStage === 'accepted' || complianceStage === 'in_progress' || complianceStage === 'completed' ? 'active' : ''} ${complianceStage !== 'pending' && complianceStage !== 'accepted' ? 'completed' : ''}`}>
                  <div className="stage-circle">2</div>
                  <div className="stage-label">Accepted</div>
                </div>
                <div className="stage-connector"></div>
                <div className={`stage-step ${complianceStage === 'in_progress' || complianceStage === 'completed' ? 'active' : ''} ${complianceStage === 'completed' ? 'completed' : ''}`}>
                  <div className="stage-circle">3</div>
                  <div className="stage-label">In Progress</div>
                </div>
                <div className="stage-connector"></div>
                <div className={`stage-step ${complianceStage === 'completed' ? 'active completed' : ''}`}>
                  <div className="stage-circle">✓</div>
                  <div className="stage-label">Completed</div>
                </div>
              </div>

              {/* Workflow Status Message */}
              <div style={{
                padding: '12px 16px',
                backgroundColor: complianceStage === 'pending' ? '#fef3c7' : complianceStage === 'accepted' ? '#dbeafe' : complianceStage === 'in_progress' ? '#e0e7ff' : '#d1fae5',
                borderLeft: `4px solid ${complianceStage === 'pending' ? '#f59e0b' : complianceStage === 'accepted' ? '#3b82f6' : complianceStage === 'in_progress' ? '#6366f1' : '#10b981'}`,
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '13px',
                fontWeight: '500'
              }}>
                {complianceStage === 'pending' && (
                  <div>
                    <strong>🟡 Current Stage: Pending</strong>
                    <p style={{ margin: '6px 0 0 0', opacity: 0.85 }}>
                      This compliance task is awaiting your acceptance. Click the <strong>Accept</strong> button below to proceed to the next stage.
                    </p>
                  </div>
                )}
                {complianceStage === 'accepted' && (
                  <div>
                    <strong>🔵 Current Stage: Accepted</strong>
                    <p style={{ margin: '6px 0 0 0', opacity: 0.85 }}>
                      You have accepted this task. Click <strong>Mark as In Progress</strong> to begin working on the compliance checklist.
                    </p>
                  </div>
                )}
                {complianceStage === 'in_progress' && (
                  <div>
                    <strong>🟣 Current Stage: In Progress</strong>
                    <p style={{ margin: '6px 0 0 0', opacity: 0.85 }}>
                      Complete all checklist items below and add your remarks. Click <strong>Mark Completed</strong> when done.
                    </p>
                  </div>
                )}
                {complianceStage === 'completed' && (
                  <div>
                    <strong>🟢 Current Stage: Completed</strong>
                    <p style={{ margin: '6px 0 0 0', opacity: 0.85 }}>
                      This compliance task has been successfully completed. All details are locked.
                    </p>
                  </div>
                )}
              </div>

              {/* Asset Status and Info */}
              <div className="modal-badges-row">
                <div className="modal-badge-group">
                  <span className="modal-badge-label">Status</span>
                  <span className={`modal-badge ${getComplianceStatus(selectedAsset) === 'critical' ? 'modal-badge-critical' : getComplianceStatus(selectedAsset) === 'warning' ? 'modal-badge-warning' : 'modal-badge-upcoming'}`}>
                    {getComplianceStatus(selectedAsset).toUpperCase()}
                  </span>
                </div>
                <div className="modal-badge-group">
                  <span className="modal-badge-label">Due Date</span>
                  <span className="modal-badge modal-badge-info">
                    {formatDate(selectedAsset.complianceDate)}
                  </span>
                </div>
                <div className="modal-badge-group">
                  <span className="modal-badge-label">Days Left</span>
                  <span className="modal-badge modal-badge-info">
                    {getDaysUntil(selectedAsset.complianceDate)} days
                  </span>
                </div>
              </div>

              {/* Asset Info Card */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">Asset Information</h4>
                <div className="modal-info-grid">
                  <div className="modal-info-item">
                    <span className="info-label">Asset Name</span>
                    <span className="info-value">{selectedAsset.assetName}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="info-label">Serial Number</span>
                    <span className="info-value">{selectedAsset.serialNumber}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="info-label">Category</span>
                    <span className="info-value">{selectedAsset.category || 'N/A'}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="info-label">Location</span>
                    <span className="info-value">{selectedAsset.labLocation || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Compliance Checklist Card */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">
                  Compliance Checklist 
                  {complianceChecklistItems && complianceChecklistItems.length > 0 && (
                    <span className="checklist-pct">{getComplianceChecklistProgress()}%</span>
                  )}
                </h4>
                
                {complianceChecklistItems && complianceChecklistItems.length > 0 ? (
                  <>
                    <div className="checklist-progress">
                      <div className="progress-bar" style={{ width: `${getComplianceChecklistProgress()}%` }}></div>
                    </div>
                    <div className="checklist-interactive">
                      {complianceChecklistItems.map((item, idx) => (
                        <div key={idx} className="checklist-item">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                            <input
                              type="checkbox"
                              id={`compliance-checklist-${idx}`}
                              checked={item.completed || false}
                              onChange={() => handleToggleComplianceChecklistItem(idx)}
                              disabled={complianceStage === 'completed'}
                            />
                            <label htmlFor={`compliance-checklist-${idx}`} className={item.completed ? 'completed' : ''} style={{ margin: 0 }}>
                              {item.text}
                            </label>
                          </div>
                          <button
                            onClick={() => handleRemoveComplianceChecklistItem(idx)}
                            disabled={complianceStage === 'completed'}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: complianceStage === 'completed' ? '#f3f4f6' : '#fee2e2',
                              color: complianceStage === 'completed' ? '#9ca3af' : '#dc2626',
                              border: complianceStage === 'completed' ? '1px solid #e5e7eb' : '1px solid #fecaca',
                              borderRadius: '4px',
                              cursor: complianceStage === 'completed' ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                            title="Remove item"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    color: '#6b7280'
                  }}>
                    <p style={{ margin: 0 }}>📋 No compliance checklist created yet</p>
                    <small style={{ color: '#9ca3af' }}>Checklist will appear here once created during asset setup</small>
                  </div>
                )}
              </div>

              {/* Remarks Section */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">Technician Remarks</h4>
                <textarea
                  value={complianceRemarks}
                  onChange={(e) => setComplianceRemarks(e.target.value)}
                  disabled={complianceStage === 'completed'}
                  placeholder="Add any compliance-related remarks or findings..."
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    resize: 'vertical',
                    opacity: complianceStage === 'completed' ? 0.6 : 1
                  }}
                />
              </div>

              {/* Additional Info */}
              {selectedAsset.complianceNotes && (
                <div className="modal-info-card modal-remarks-card">
                  <h4 className="modal-card-title">Previous Notes</h4>
                  <p className="modal-description">{selectedAsset.complianceNotes}</p>
                </div>
              )}
            </div>

            {/* Modal Footer with Actions */}
            <div className="modal-footer">
              {complianceStage === 'pending' && (
                <>
                  <button
                    className="btn btn-accept"
                    onClick={handleAcceptCompliance}
                    disabled={loading}
                    title="Accept compliance task to move to next stage"
                  >
                    ✓ Accept
                  </button>
                  <button
                    className="btn btn-reject"
                    onClick={() => setShowRejectModal(true)}
                    disabled={loading}
                    title="Reject this compliance task"
                  >
                    ✕ Reject
                  </button>
                  <button
                    className="btn btn-close"
                    onClick={handleCloseDetailModal}
                  >
                    Close
                  </button>
                </>
              )}

              {complianceStage === 'accepted' && (
                <>
                  <button
                    className="btn btn-progress"
                    onClick={handleMarkAsInProgress}
                    disabled={loading || getComplianceChecklistProgress() === 0}
                    title="Start working on compliance checklist"
                  >
                    ⚙️ Mark as In Progress
                  </button>
                  <button
                    className="btn btn-reject"
                    onClick={() => setShowRejectModal(true)}
                    disabled={loading}
                    title="Reject after accepting (you can change your mind)"
                  >
                    ✕ Reject
                  </button>
                  <button
                    className="btn btn-close"
                    onClick={handleCloseDetailModal}
                  >
                    Close
                  </button>
                </>
              )}

              {complianceStage === 'in_progress' && (
                <>
                  <button
                    className="btn btn-submit"
                    onClick={handleMarkAsComplete}
                    disabled={loading}
                    title={getComplianceChecklistProgress() === 100 ? 'All items checked - Complete compliance' : 'Mark as complete (unchecked items will be ignored)'}
                    style={{
                      backgroundColor: getComplianceChecklistProgress() === 100 ? '#10b981' : '#3b82f6'
                    }}
                  >
                    ✅ Mark as Complete {getComplianceChecklistProgress() === 100 && '(All Done)'}
                  </button>
                  <button
                    className="btn btn-reject"
                    onClick={() => setShowRejectModal(true)}
                    disabled={loading}
                    title="Reject if you can't continue"
                  >
                    ✕ Reject
                  </button>
                  <button
                    className="btn btn-close"
                    onClick={handleCloseDetailModal}
                  >
                    Close
                  </button>
                </>
              )}

              {complianceStage === 'completed' && (
                <button
                  className="btn btn-close"
                  onClick={handleCloseDetailModal}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-icon" style={{ fontSize: '24px', color: '#ef4444' }}><FaCross /></div>
                <div>
                  <h3>Reject Compliance Task</h3>
                  <div className="modal-subtitle">Provide a reason for rejection</div>
                </div>
              </div>
              <button className="close-btn" onClick={() => setShowRejectModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Rejection Reason *</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why you're rejecting this compliance task (e.g., equipment is not available, need more time, etc.)"
                  rows="5"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
                <small style={{ color: '#6b7280', marginTop: '6px', display: 'block' }}>
                  This reason will be shown to the admin for review
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-cancel"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-reject"
                onClick={handleRejectCompliance}
                disabled={loading || !rejectionReason.trim()}
              >
                {loading ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompliancePage;
